import React, { useEffect, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';
// import './MrnAccordion.css'; // Create this CSS file

function CommonMrnFields({ onDataChange, initialData }) {
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Main fields
    const [podate, setPodate] = useState(initialData?.podate || '');
    const [receivedate, setreceivedate] = useState(initialData?.receivedate || '');
    const [pono, setPono] = useState(initialData?.pono || '');
    const [status, setStatus] = useState(initialData?.status || '');
    const [shipto, setShipto] = useState(initialData?.shipto || '');
    const [deliverynoteno, setdeliverynoteno] = useState(initialData?.deliverynoteno || '');
    const [deliverynotedate, setdeliverynotedate] = useState(initialData?.deliverynotedate || '');

    // Supplier related fields
    const [supplierSearch, setSupplierSearch] = useState(encodeURIComponent(" "));
    const [supplier, setSupplier] = useState(initialData?.supplier || null);
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [supplierDetails, setSupplierDetails] = useState({
        contactperson: initialData?.supplierDetails?.contactperson || '',
        contactno: initialData?.supplierDetails?.contactno || '',
        email: initialData?.supplierDetails?.email || '',
    });
    const [supplierAddresses, setSupplierAddresses] = useState([]);
    const [supplierMultipleAddress, setSupplierMultipleAddress] = useState(initialData?.supplierMultipleAddress || null);

    // Branch and warehouse
    const [branch, setBranch] = useState(initialData?.branch || null);
    const [branchOptions, setBranchOptions] = useState([]);
    const [warehouse, setWarehouse] = useState(initialData?.warehouse || null);
    const [warehouseOptions, setWarehouseOptions] = useState([]);

    // Accordion states
    const [isBranchWarehouseOpen, setIsBranchWarehouseOpen] = useState(false);
    const [isSupplierOpen, setIsSupplierOpen] = useState(false);

    // Search handlers with debounce
    const handleSearchChangeSupplier = debounce((inputValue) => {
        setSupplierSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 300);

    // Data fetch functions
    const getSupplierData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}supplier/page/0/10/asc/id/${supplierSearch}`,
                { headers: authHeader() }
            );

            const suppliers = response.data.Index || response.data.data || [];
            setSupplierOptions(
                suppliers.map(s => ({
                    value: s.id,
                    label: s.companyname,
                    contactperson: s.contactperson,
                    contactno: s.contactno,
                    email: s.email,
                }))
            );
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch suppliers. Please try again.',
            });
        }
    };

    const getSupplierDetails = async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}supplier/findById/${id}`, {
                headers: authHeader(),
            });
            const data = response.data;
            setSupplierDetails({
                contactperson: data.contactperson,
                contactno: data.contactno,
                email: data.email,
            });
            getSupplierAddresses(id);
        } catch (error) {
            console.error("Error fetching supplier details", error);
        }
    };

    const getSupplierAddresses = async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}supplierMultipleAddress/getAddress/${id}`, {
                headers: authHeader(),
            });
            setSupplierAddresses(response.data.map(addr => ({
                value: addr.id,
                label: addr.supplieraddress,
            })));
        } catch (error) {
            console.error("Error fetching supplier addresses", error);
        }
    };

    const getBranchData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}branch/findAllBranch`, {
                headers: authHeader(),
            });
            setBranchOptions(response.data.map(b => ({ value: b.id, label: b.branchname })));
        } catch (error) {
            console.error("Error fetching branches", error);
        }
    };

    const getWarehouseData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                headers: authHeader(),
            });
            setWarehouseOptions(response.data.map(w => ({ value: w.id, label: w.warehousename })));
        } catch (error) {
            console.error("Error fetching warehouses", error);
        }
    };

    // Change handlers
    const handleSupplierChange = (selected) => {
        setSupplier(selected);
        setSupplierMultipleAddress(null);
        if (selected) {
            getSupplierDetails(selected.value);
        } else {
            setSupplierDetails({
                contactperson: '',
                contactno: '',
                email: '',
            });
            setSupplierAddresses([]);
        }
    };

    const handleSupplierAddressChange = (selected) => {
        setSupplierMultipleAddress(selected);
    };

    const handleWarehouseChange = async (selected) => {
        setWarehouse(selected);
        if (selected) {
            try {
                const response = await axios.get(`${BASE_URL}warehouse/findById/${selected.value}`, {
                    headers: authHeader(),
                });
                setShipto(response.data.address || '');
            } catch (error) {
                console.error("Error fetching warehouse address", error);
            }
        } else {
            setShipto('');
        }
    };

    const handleBranchChange = (selected) => {
        setBranch(selected);
    };

    // Effect hooks
    useEffect(() => {
        getBranchData();
        getWarehouseData();
        if (initialData?.supplier?.value) {
            getSupplierDetails(initialData.supplier.value);
        }
    }, []);

    useEffect(() => {
        getSupplierData();
    }, [supplierSearch]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                podate,
                receivedate,
                pono,
                status,
                shipto,
                deliverynoteno,
                deliverynotedate,
                supplier,
                supplierDetails,
                supplierMultipleAddress,
                branch,
                warehouse
            });
        }
    }, [
        podate, receivedate, pono, status, shipto, deliverynoteno, deliverynotedate,
        supplier, supplierDetails, supplierMultipleAddress,
        branch, warehouse
    ]);

    const toggleBranchWarehouseSection = () => {
        setIsBranchWarehouseOpen(!isBranchWarehouseOpen);
    };

    const toggleSupplierSection = () => {
        setIsSupplierOpen(!isSupplierOpen);
    };

    return (
        <>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>PO No.</Form.Label>
                        <Form.Control
                            type="text"
                            value={pono}
                            onChange={(e) => setPono(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>PO Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={podate}
                            onChange={(e) => setPodate(e.target.value)}
                        />
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Note No. </Form.Label>
                        <Form.Control
                            type="text"
                            value={deliverynoteno}
                            onChange={(e) => setdeliverynoteno(e.target.value)}
                        />
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Delivery Note Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={deliverynotedate}
                            onChange={(e) => setdeliverynotedate(e.target.value)}
                        />
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Received Date</Form.Label>
                        <Form.Control
                            type='date'
                            value={receivedate}
                            onChange={(e) => setreceivedate(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>



            {/* Supplier Custom Accordion */}
            <div className="supplier-accordion-container">
                <div
                    className={`supplier-accordion-header ${isSupplierOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={toggleSupplierSection}
                >
                    <h7 className="mb-0">Supplier Details</h7>
                    <span className="supplier-accordion-icon">
                        {isSupplierOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${isSupplierOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Supplier <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={supplier}
                                    onChange={handleSupplierChange}
                                    onInputChange={handleSearchChangeSupplier}
                                    options={supplierOptions}
                                    placeholder="Search supplier"
                                    className={formErrors.supplier ? 'is-invalid' : ''}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Contact Person</Form.Label>
                                <Form.Control
                                    value={supplierDetails.contactperson}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Contact Number</Form.Label>
                                <Form.Control
                                    value={supplierDetails.contactno}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    value={supplierDetails.email}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Supplier Address <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={supplierMultipleAddress}
                                    onChange={handleSupplierAddressChange}
                                    options={supplierAddresses}
                                    placeholder="Select address"
                                    isClearable
                                    className={formErrors.supplierMultipleAddress ? 'is-invalid' : ''}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Branch & Warehouse Custom Accordion */}
            <div className="supplier-accordion-container">
                <div
                    className={`supplier-accordion-header ${isBranchWarehouseOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={toggleBranchWarehouseSection}
                >
                    <h7 className="mb-0">Branch & Warehouse Details</h7>
                    <span className="supplier-accordion-icon">
                        {isBranchWarehouseOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${isBranchWarehouseOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Branch</Form.Label>
                                <Select
                                    value={branch}
                                    onChange={handleBranchChange}
                                    options={branchOptions}
                                    placeholder="Select branch"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Warehouse</Form.Label>
                                <Select
                                    value={warehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder="Select warehouse"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Delivery Address:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={shipto}
                                    onChange={(e) => setShipto(e.target.value)}
                                    placeholder="Shipping address"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

        </>
    );
}

export default CommonMrnFields;